import React, { memo } from "react";
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Stethoscope, CalendarCheck } from "lucide-react";

// Icon mapping for different stat types
const STAT_ICONS = {
  total: Stethoscope,
  available: CalendarCheck
};

// Icon tile tone per stat type
const STAT_TONES = {
  total: 'bg-[#EFF6FF] text-[#2563EB]',
  available: 'bg-[#F0FDFA] text-[#0D9488]'
};

// Default stats structure for mapping API data to display
const getStatsFromApiData = (statsData) => {
  if (!statsData) return [];

  const statsConfig = [
    { id: 'total', dataKey: 'totalPractitioners', title: 'Total Practitioners' },
    { id: 'available', dataKey: 'practitionersAvailableToday', title: 'Practitioners Available Today' }
  ];

  return statsConfig.map(config => ({
    id: config.id,
    icon: STAT_ICONS[config.id],
    value: statsData[config.dataKey] || 0,
    title: config.title
  }));
};

// Reusable class constants
const CLASSES = {
  CARD: 'flex flex-col w-full min-w-0 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl p-3 shadow-sm cursor-pointer hover:shadow-md transition-shadow',
  CARD_SELECTED: 'border-[#2563EB] ring-2 ring-[#BFDBFE]',
  ICON_CONTAINER: 'flex items-center justify-center w-[38px] h-[38px] rounded-xl mb-1.5',
  ICON: 'w-[18px] h-[18px]',
  VALUE: 'text-[26px] leading-tight font-bold text-[#171717] font-poppins',
  TITLE: 'mt-0.5 text-[13px] uppercase tracking-wide font-medium text-[#64748B] font-poppins'
};

const StatCard = memo(({ icon: Icon, value, title, id, onClick, isSelected }) => (
  <div
    className={`${CLASSES.CARD} ${isSelected ? CLASSES.CARD_SELECTED : ''}`}
    onClick={() => onClick && onClick(id)}
    role="button"
    tabIndex={0}
    aria-pressed={isSelected}
    aria-label={`Filter by ${title.toLowerCase()}`}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick && onClick(id);
      }
    }}
  >
    {/* Icon in Circle */}
    <div className={`${CLASSES.ICON_CONTAINER} ${STAT_TONES[id] || STAT_TONES.total}`}>
      {typeof Icon === 'string' ? (
        <img
          src={Icon}
          alt={`${title} icon`}
          className={CLASSES.ICON}
          loading="lazy"
        />
      ) : (
        <Icon size={18} />
      )}
    </div>

    {/* Text Section */}
    <div>
      <div className={CLASSES.VALUE} aria-label={`${value} ${title.toLowerCase()}`}>
        {value.toLocaleString()}
      </div>
      <div className={CLASSES.TITLE}>{title}</div>
    </div>
  </div>
));

StatCard.displayName = 'StatCard';

StatCard.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]).isRequired,
  value: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  isSelected: PropTypes.bool
};

function PractitionerCounter({ stats, onStatClick, selectedStatId }) {
  // Get stats data from Redux
  const { data: apiStatsData, loading, error } = useSelector(state => state.practitioners.stats);

  // Use provided stats prop, or derive from Redux data, or show loading/error states
  const displayStats = stats || getStatsFromApiData(apiStatsData);

  if (loading) {
    const LoadingSkeleton = () => (
      <div className="flex flex-col gap-1.5 w-full min-w-0 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl p-3 shadow-sm animate-pulse">
        <div className="w-[38px] h-[38px] bg-[#F1F5F9] rounded-xl"></div>
        <div>
          <div className="h-7 bg-[#F1F5F9] rounded w-16 mb-1"></div>
          <div className="h-4 bg-[#F1F5F9] rounded w-20"></div>
        </div>
      </div>
    );

    return (
      <div className="p-0 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
          {Array.from({ length: 2 }, (_, i) => (
            <LoadingSkeleton key={`skeleton-${i}`} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-0 mb-4">
        <div className="text-[#DC2626] text-sm font-poppins text-center p-4">
          Error loading practitioner statistics: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-0 mb-2">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 p-1">
        {displayStats.map((stat) => (
          <StatCard
            key={stat.id}
            id={stat.id}
            icon={stat.icon}
            value={stat.value}
            title={stat.title}
            onClick={onStatClick}
            isSelected={selectedStatId === stat.id}
          />
        ))}
      </div>
    </div>
  );
}

PractitionerCounter.propTypes = {
  stats: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      icon: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]).isRequired,
      value: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired
    })
  ),
  onStatClick: PropTypes.func,
  selectedStatId: PropTypes.string
};

export default PractitionerCounter;
